import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, ElementRef, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReportService } from '../../services/report.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, switchMap } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { PdfViewerComponent as NgxPdfViewer } from 'ng2-pdf-viewer';
import { saveAs } from 'file-saver';
import { DocumentData } from '../../services/document-data.service';
import { CdkDragEnd } from '@angular/cdk/drag-drop';

interface S3Response {
  url: string;
  [key: string]: any;
}

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss']
})
export class PdfViewerComponent implements OnInit, OnDestroy {
  @ViewChild(NgxPdfViewer) private pdfComponent!: NgxPdfViewer;
  private destroy$ = new Subject<void>();
  private defaultPosition = { x: 0, y: 0 };
  dockPosition = { ...this.defaultPosition };

  isSnappedLeft = false;
  isSnappedRight = false;
  isNearLeftZone = false;
  isNearRightZone = false;
  isInLeftZone = false;
  isInRightZone = false;

  pdfSrc: string | Uint8Array = '';
  loading: boolean = true;
  zoom: number = 1;
  currentPage: number = 1;
  totalPages: number = 0;
  submissionId: string = '';
  private isMobileView = false;
  isDockExpanded = false;

  constructor(
    private route: ActivatedRoute,
    private reportService: ReportService,
    private http: HttpClient,
    private router: Router,
    private documentDataService: DocumentData
  ) { }

  @HostListener('window:resize')
  onResize() {
    this.updateSnapZoneWidths();
    this.checkMobileView();
    if (window.innerWidth > 500) {
      this.isDockExpanded = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (window.innerWidth <= 500) {
      const dock = document.querySelector('.floating-dock');
      if (dock && !dock.contains(event.target as Node) && this.isDockExpanded) {
        this.isDockExpanded = false;
      }
    }
  }

  ngOnInit(): void {
    this.checkMobileView();
    const fileUrl = this.route.snapshot.queryParamMap.get('fileUrl') || localStorage.getItem('lastPdfUrl');
    this.submissionId = this.extractSubmissionId(fileUrl || '');
    if (fileUrl) {
      this.loadPdf(fileUrl);
    }

    // Calculate initial dock position
    setTimeout(() => {
      const container = document.querySelector('.pdf-viewer-container');
      const dock = document.querySelector('.floating-dock');
      if (container && dock) {
        if (this.isMobileView) {
          this.snapToLeft();
        } else {
        const containerRect = container.getBoundingClientRect();
        const dockRect = dock.getBoundingClientRect();
        this.dockPosition = {
          x: (containerRect.width - dockRect.width) / 2,
          y: containerRect.height - dockRect.height - 20
        };
        }
      }
    }, 0);

    // Initial calculation of snap zone widths
    setTimeout(() => this.updateSnapZoneWidths(), 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private extractSubmissionId(url: string): string {
    const matches = url.match(/submissions\/([^\/]+)/);
    return matches ? matches[1] : '';
  }

  private loadPdf(url: string) {
    this.loading = true;
    this.http.get(url, { responseType: 'blob' })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 403) {
            // If we get a 403, the URL has expired. Get a fresh URL from the service
            return this.reportService.getS3Json(url).pipe(
              switchMap((response: S3Response) => {
                // Use the new URL to fetch the PDF
                return this.http.get(response.url, { responseType: 'blob' });
              })
            );
          }
          throw error;
        })
      )
      .subscribe({
        next: (pdfBlob: Blob) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const arrayBuffer = reader.result as ArrayBuffer;
            console.log('arrayBuffer', arrayBuffer);
            this.pdfSrc = new Uint8Array(arrayBuffer);
            this.loading = false;

            // Save URL to localStorage before removing from query params
            localStorage.setItem('lastPdfUrl', url);

            // Remove the fileUrl query parameter after successful load
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { fileUrl: null },
              queryParamsHandling: 'merge'
            });

            // Update snap zone widths after PDF loads
            setTimeout(() => this.updateSnapZoneWidths(), 100);
          };
          reader.readAsArrayBuffer(pdfBlob);
        },
        error: (error) => {
          console.error('Error loading PDF:', error);
          this.loading = false;
          // Clear localStorage on error
          localStorage.removeItem('lastPdfUrl');
        }
      });
  }

  onPagesLoaded(pages: any): void {
    this.totalPages = pages.numPages;
  }

  zoomIn(): void {
    if (this.zoom < 3) {
      this.zoom += 0.1;
    }
  }

  zoomOut(): void {
    if (this.zoom > 0.5) {
      this.zoom -= 0.1;
    }
  }

  resetZoom(): void {
    this.zoom = 1;
  }

  goToPage(page: number): void {
    const pageNum = Number(page);
    if (pageNum >= 1 && pageNum <= this.totalPages) {
      this.currentPage = pageNum;
      if (this.pdfComponent) {
        this.pdfComponent.page = pageNum;
        setTimeout(() => {
          const pageElement = document.querySelector(`[data-page-number="${pageNum}"]`);
          if (pageElement) {
            pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  // downloadPdf(): void {
  //   if (this.submissionId) {
  //     this.reportService.downloadOriginalDocument(this.submissionId)
  //       .subscribe(
  //         (data: any) => {
  //           saveAs(data.urlToDownload, 'document.pdf');
  //         },
  //         (error: any) => {
  //           console.error('Error downloading PDF:', error);
  //         }
  //       );
  //   }
  // }

  onDragMoved(event: any): void {
    const element = event.source.element.nativeElement;
    const rect = element.getBoundingClientRect();
    const containerRect = document.querySelector('.pdf-viewer-container')?.getBoundingClientRect();

    if (containerRect) {
      // Check proximity to left zone (within 50px)
      this.isNearLeftZone = rect.left < 150 && rect.top < 200;
      this.isInLeftZone = rect.left < 100 && rect.top < 150;

      // Check proximity to right zone (within 50px)
      this.isNearRightZone = rect.right > (containerRect.right - 150) && rect.top < 200;
      this.isInRightZone = rect.right > (containerRect.right - 100) && rect.top < 150;
    }
  }

  onDragEnded(event: CdkDragEnd): void {
    // Prevent drag end handling on mobile
    if (this.isMobileView) {
      return;
    }

    const element = event.source.element.nativeElement;
    const rect = element.getBoundingClientRect();
    const containerRect = document.querySelector('.pdf-viewer-container')?.getBoundingClientRect();

    if (containerRect) {
      // Reset all zone states
      this.isNearLeftZone = false;
      this.isNearRightZone = false;
      this.isInLeftZone = false;
      this.isInRightZone = false;

      // Check if dock is in left snap zone
      if (rect.left < 100 && rect.top < 150) {
        this.snapToLeft();
        return;
      }

      // Check if dock is in right snap zone
      if (rect.right > (containerRect.right - 100) && rect.top < 150) {
        this.snapToRight();
        return;
      }

      // Update position if not snapped
      this.dockPosition = {
        x: this.dockPosition.x + event.distance.x,
        y: this.dockPosition.y + event.distance.y
      };
    }
  }

  private snapToLeft(): void {
    this.isSnappedLeft = true;
    this.isSnappedRight = false;
    this.dockPosition = { x: 10, y: 20 };
    this.showSnapZoneHighlight('left');
  }

  private snapToRight(): void {
    this.isSnappedRight = true;
    this.isSnappedLeft = false;
    const container = document.querySelector('.pdf-viewer-container');
    const dock = document.querySelector('.floating-dock');
    if (container && dock) {
      const containerRect = container.getBoundingClientRect();
      const dockRect = dock.getBoundingClientRect();
      this.dockPosition = {
        x: containerRect.width - dockRect.width - 10,
        y: 20
      };
    }
    this.showSnapZoneHighlight('right');
  }

  private showSnapZoneHighlight(side: 'left' | 'right'): void {
    const zone = document.querySelector(`.snap-zone-${side}`);
    zone?.classList.add('active');
    setTimeout(() => {
      zone?.classList.remove('active');
    }, 300);
  }

  unpin(): void {
    // Prevent unpinning on mobile
    if (this.isMobileView) {
      return;
    }
    const container = document.querySelector('.pdf-viewer-container');
    const dock = document.querySelector('.floating-dock') as HTMLElement;
    const pdfContent = document.querySelector('.pdf-content');

    if (container && dock && pdfContent) {
      const containerRect = container.getBoundingClientRect();
      const dockRect = dock.getBoundingClientRect();
      const pdfRect = pdfContent.getBoundingClientRect();

      dock.style.transition = 'all 0.3s ease';

      // Calculate the space between PDF and screen edges
      const leftSpace = pdfRect.left - containerRect.left;
      const rightSpace = containerRect.right - pdfRect.right;

      // Calculate new position based on which side it was snapped to
      let newX = 0;
      if (this.isSnappedLeft) {
        // Position in the left space, but not too close to the PDF
        newX = Math.max(leftSpace * 0.5 - dockRect.width / 2, 20);
      } else if (this.isSnappedRight) {
        // Position in the right space, but not too close to the PDF
        newX = Math.max(rightSpace * 0.5 - dockRect.width / 2, 20);
      }

      // Keep the same vertical position but ensure it's within bounds
      const newY = Math.min(
        Math.max(dockRect.top - containerRect.top, 20),
        containerRect.height - dockRect.height
      );

      this.dockPosition = {
        x: newX - 50,
        y: newY - 20
      };

      // Reset snap states after calculating position
      this.isSnappedLeft = false;
      this.isSnappedRight = false;

      setTimeout(() => {
        dock.style.transition = '';
      }, 300);
    }
  }

  private updateSnapZoneWidths(): void {
    const container = document.querySelector('.pdf-viewer-container');
    const pdfContent = document.querySelector('.pdf-content');

    if (container && pdfContent) {
      const containerRect = container.getBoundingClientRect();
      const pdfRect = pdfContent.getBoundingClientRect();

      // Calculate the space between PDF and screen edges
      const leftWidth = Math.max(pdfRect.left - containerRect.left, 0);
      const rightWidth = Math.max(containerRect.right - pdfRect.right, 0);

      // Update snap zone widths
      const leftZone = document.querySelector('.snap-zone-left') as HTMLElement;
      const rightZone = document.querySelector('.snap-zone-right') as HTMLElement;

      if (leftZone) {
        leftZone.style.width = `${leftWidth}px`;
      }
      if (rightZone) {
        rightZone.style.width = `${rightWidth}px`;
      }
    }
  }

  downloadPdf() {
    if (!this.pdfSrc) {
      return;
    }

    // Extract filename from fileUrl
    const fileUrl = this.route.snapshot.queryParamMap.get('fileUrl') || localStorage.getItem('lastPdfUrl') || '';
    console.log('fileUrl', fileUrl);
    const fileName = fileUrl.split('.pdf')[0].split('/').pop() || 'document.pdf';
    console.log(fileName, 'fileName');


    // Handle both string URL and Uint8Array cases
    if (typeof this.pdfSrc === 'string') {
      // For URL, fetch the PDF first
      this.http.get(this.pdfSrc, { responseType: 'blob' })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            console.error('Error downloading PDF:', error);
            return of(null);
          })
        )
        .subscribe(blob => {
          if (blob) {
            saveAs(blob, fileName);
          }
        });
    } else {
      // For Uint8Array, convert to blob directly
      const blob = new Blob([this.pdfSrc], { type: 'application/pdf' });
      saveAs(blob, fileName);
    }
  }

  private checkMobileView(): void {
    const wasMobile = this.isMobileView;
    this.isMobileView = window.innerWidth <= 768;

    // If transitioning to mobile view, snap to left
    if (!wasMobile && this.isMobileView) {
      this.snapToLeft();
    }
    // If transitioning from mobile to desktop and was snapped, unpin
    else if (wasMobile && !this.isMobileView && (this.isSnappedLeft || this.isSnappedRight)) {
      this.unpin();
    }
  }

  toggleDockExpansion(event: Event): void {
    if (window.innerWidth <= 500) {
      event.stopPropagation();
      this.isDockExpanded = !this.isDockExpanded;
    }
  }
}

